import React, { useEffect, useState } from 'react';
import { fetchRandomEvent } from './api/wikipedia';
import GuessInput from './components/GuessInput';
import './styles/App.css';

function App() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userGuess, setUserGuess] = useState(null);
  //const [score, setScore] = useState(null);
  const [difference, setDifference] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);

  useEffect(() => {
    loadEvent();
  }, []);

  const loadEvent = async () => {
    setLoading(true);
    setImageLoading(true);
    
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const sharedEvent = urlParams.get('event');
    
    if (sharedEvent) {
      try {
        const eventData = JSON.parse(decodeURIComponent(sharedEvent));
        setEvent(eventData);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Error parsing shared event:', error);
      }
    }
    
    const data = await fetchRandomEvent();
    setEvent(data);
    setUserGuess(null);
    //setScore(null);
    setDifference(null);
    setLoading(false);
  };

  const handleGuess = (guessYear) => {
    setUserGuess(guessYear);
    const actualYear = event.year;
    const difference = guessYear - actualYear;
    setDifference(difference);
    //const points = Math.max(0, 100 - Math.abs(difference) * 10);
    //setScore(points);
  };

  const handleNewGame = () => {
    // Only load new event if explicitly called
    if (userGuess !== null) {
      loadEvent();
    }
  };

  const getShareText = () => {
    if (userGuess !== null) {
      return `Think you're good at history? Challenging you to a game of History Hunt, the GeoGuessr for history!`;
    } else {
      // Create a shareable URL with the current event data
      const eventData = encodeURIComponent(JSON.stringify({
        text: event.text,
        year: event.year,
        imageUrl: event.imageUrl,
        wikipediaUrl: event.wikipediaUrl
      }));
      const shareUrl = `${window.location.origin}${window.location.pathname}?event=${eventData}`;
      
      return `When do you think this happened? \n\n${event.text}\n\nMake your guess on History Hunt: ${shareUrl}`;
    }
  };

  const handleShare = async (platform) => {
    const shareText = getShareText();
    const shareUrl = window.location.href;

    if (platform === 'instagram') {
      try {
        // Create URL with event data
        const eventData = encodeURIComponent(JSON.stringify({
          text: event.text,
          year: event.year,
          imageUrl: event.imageUrl,
          wikipediaUrl: event.wikipediaUrl
        }));
        const challengeUrl = `${window.location.origin}${window.location.pathname}?event=${eventData}`;
        
        await navigator.clipboard.writeText(challengeUrl);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
        return;
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }

    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: userGuess !== null ? 'History GeoGuessr Score' : 'History GeoGuessr Challenge',
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
      email: `mailto:?subject=A Challenge in Time&body=${encodeURIComponent(shareText.replace(shareUrl,shareUrl))}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="main-container">
      <h1 className="heading">
        <a href="/">History Hunt</a>
        <p className="description">GeoGuessr meets history</p>
      </h1>
      {/* Loading Placeholder*/}
      {loading && <p className="loading-placeholder">⌛️Spinning up the time machine⌛️</p>}

      {!loading && event && !userGuess && (
        <div className={`content-container ${!event.imageUrl ? 'no-image' : ''}`}>
          <div className="left-column">
            <p className="event-text">{event.text}</p>
            <GuessInput onSubmit={handleGuess} />
          </div>

          {event.imageUrl && (
            <div className="right-column">
              <img 
                src={event.imageUrl}
                className="event-image"
                onLoad={() => setImageLoading(false)}
                style={{ opacity: imageLoading ? 0.5 : 1 }}
              />
            </div>
          )}
        </div>
      )}

      {!loading && event && !userGuess && (
        <div className="challenge-container">
          <div className="challenge-button-container">
            <button 
              onClick={() => setShowShareOptions(!showShareOptions)}
              className="challenge-button"
            >
              Challenge a friend
            </button>
            
            <div className={`share-options ${showShareOptions ? 'show' : ''}`}>
              <button 
                onClick={() => handleShare('instagram')} 
                className="share-option-button"
                style={{
                  color: copySuccess ? '#4CAF50' : '#000',
                }}
              >
                {copySuccess ? 'Copied!' : 'Copy Link'}
              </button>
              <button onClick={() => handleShare('email')} className="share-option-button">
                Email
              </button>
              <button onClick={() => handleShare('whatsapp')} className="share-option-button">
                WhatsApp
              </button>
              <button onClick={() => handleShare('twitter')} className="share-option-button">
                X
              </button>
            </div>
          </div>
          <div className="social-links">
            <a href="https://twitter.com/historyhunt_dev" className="social-link" target="_blank" rel="noopener noreferrer">@historyhunt_dev</a>
            <a 
              href="https://buy.stripe.com/3cs4jZdfZ45z3Mk144"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
             Support 🪙
            </a>
          </div>
        </div>
      )}

      {userGuess !== null && (
        <div className={`content-container ${!event.imageUrl ? 'no-image' : ''}`}>
          <div className="left-column">
            <div className="result-container">
              <p className="event-text">{event.text}</p>
              <p>This happened in {event.year}.</p>
              {difference === 0 ? (
                <p>Right on the money! 🎯</p>
              ) : (
                <p>You are {Math.abs(difference)} years {difference > 0 ? 'late 🏃' : 'early ⏳'}</p>
              )}
            <button
            onClick={() => window.open(event.wikipediaUrl, '_blank')}
            className="challenge-button"
          >Read more about the event
          </button>
            </div>
          </div>
          {event.imageUrl && (
            <div className="right-column">
              <img 
                src={event.imageUrl} 
                alt="Event image"
                className="event-image"
              />
            </div>
          )}
        </div>
      )}

      {userGuess !== null && (
        <div className="event-answer-footer-container">
          <div className="button-row">
            <button 
              onClick={() => setShowShareOptions(!showShareOptions)}
              className="challenge-button"
            >
              Challenge a friend
            </button>
            
            <div className={`share-options ${showShareOptions ? 'show' : ''}`}>
              <button 
                onClick={() => handleShare('instagram')} 
                className="share-option-button"
                style={{
                  color: copySuccess ? '#4CAF50' : '#000',
                }}
              >
                {copySuccess ? 'Copied!' : 'Copy Link'}
              </button>
              <button onClick={() => handleShare('email')} className="share-option-button">
                Email
              </button>
              <button onClick={() => handleShare('whatsapp')} className="share-option-button">
                WhatsApp
              </button>
              <button onClick={() => handleShare('twitter')} className="share-option-button">
                X
              </button>
            </div>

            <button
              onClick={handleNewGame}
              className="challenge-button"
            >
              Next event
            </button>
          </div>
          <div className="social-links">
            <a href="https://twitter.com/historyhunt_dev" className="social-link" target="_blank" rel="noopener noreferrer">@historyhunt_dev</a>
            <a 
              href="https://buy.stripe.com/3cs4jZdfZ45z3Mk144"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              Support 🪙
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;